import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Info, MapPin } from 'lucide-react';
import { Lease } from '../../types/leases';
import LeaseHistoryButton from '../Leases/LeaseHistoryButton';
import { isUnitRented, storageUnitDisplayName } from '../../Helpers/storageUnitUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { StorageUnit } from '../../types/storageUnits';
import { Building, LevelModel } from '../../types/protocols';
import { translateStorageUnitType } from '../../types/EnumTranslations';
import InfoPanel from '../../Components/InfoPanel';
import { RentableDetailSection, RentableDetailTitle, RentableLeaseSection, RentablePhotosSection } from '../../Components/RentableDetail/RentableDetail';
import { useFeature } from '../../hooks/useFeatures';
import ObjectKeysButton from '../KeyRegister/ObjectKeysButton';
import ObjectKeysModal from '../KeyRegister/ObjectKeysModal';

interface StorageUnitDetailModalProps {
    /** False while a child modal (Redigera, Nytt hyresavtal, delete confirmation) is open. */
    show: boolean;
    onHide: () => void;
    building: Building;
    unit: StorageUnit;
    level?: LevelModel;
    isAdmin: boolean;
    submitting: boolean;
    hasDrawing: boolean;
    isPlacedOnCurrentDrawing: boolean;
    onEdit: () => void;
    onDelete: () => void;
    /** Click-to-place for a unit that has no marker on the current drawing. Placed units are moved with "Flytta markörer". */
    onStartPlace: () => void;
    /** The lease behind `unit.currentLeaseKey`, when loaded. */
    lease?: Lease;
    tenantName?: string;
    /** Hyresavtal for this object; absent for non-admins, who cannot open it. */
    leasesLink?: string;
    onNewLease: () => void;
    /** Admins only: the object's ended leases, newest first ("Tidigare hyresgäster"). */
    endedLeases?: Lease[];
    getTenantName: (tenantKey: string) => string;
}

/** Details, photos and actions for the selected storage unit; Redigera (incl. Ta bort placering) and Ta bort in the header's ⋯ menu. */
const StorageUnitDetailModal: React.FC<StorageUnitDetailModalProps> = ({
    show, onHide, building, unit, level, isAdmin, submitting, hasDrawing, isPlacedOnCurrentDrawing,
    onEdit, onDelete, onStartPlace,
    lease, tenantName, leasesLink, onNewLease, endedLeases, getTenantName
}) => {
    // Nycklar (admins, key register on): this modal steps aside while the keys modal is open; the register is read on open.
    const keyRegisterOn = useFeature('keyRegister');
    const [keysOpen, setKeysOpen] = useState(false);

    const ceilingHeight = unit.ceilingHeight ?? level?.ceilingHeight;
    const photos = unit.photos || {};
    const rented = isUnitRented(unit);

    const placementValue = !hasDrawing
        ? 'Ingen ritning'
        : isPlacedOnCurrentDrawing
            ? 'Placerad på ritningen'
            : (
                <span className="rentable-detail-inline">
                    <span style={{ color: '#7a5b00' }}>Ej placerat</span>
                    {isAdmin && (
                        <button type="button" className="rentable-detail-link" onClick={onStartPlace} disabled={submitting}>
                            <MapPin size={14} />Placera på ritningen
                        </button>
                    )}
                </span>
            );

    return (
        <>
            <Modal show={show && !keysOpen} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <RentableDetailTitle
                        name={storageUnitDisplayName(unit)}
                        status={{ rented, label: rented ? 'Uthyrt' : 'Ledigt' }}
                        onEdit={isAdmin ? onEdit : undefined}
                        onDelete={isAdmin ? onDelete : undefined}
                        deleteLabel="Ta bort förråd"
                        disabled={submitting}
                    />
                </Modal.Header>
                <Modal.Body>
                    <RentableDetailSection icon={<Info size={16} />} title="Uppgifter">
                        <InfoPanel
                            items={[
                                { label: 'Typ', value: translateStorageUnitType(unit.type) },
                                { label: 'Våning', value: level ? levelDisplayName(level) : 'Våning saknas' },
                                { label: 'Yta', value: unit.area ? `${unit.area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²` : '-' },
                                { label: 'Takhöjd', value: ceilingHeight ? `${ceilingHeight.toFixed(2)} m${unit.ceilingHeight ? '' : ' (våningens)'}` : '-' },
                                { label: 'Placering', value: placementValue },
                                ...(unit.comment ? [{ label: 'Kommentar', value: unit.comment }] : []),
                            ]}
                        />
                    </RentableDetailSection>

                    <RentableLeaseSection
                        rented={rented}
                        lease={lease}
                        tenantName={tenantName}
                        leasesLink={leasesLink}
                        rentedText="Uthyrt"
                        vacantText="Förrådet är ledigt."
                        onNewLease={isAdmin ? onNewLease : undefined}
                        submitting={submitting}
                        links={isAdmin && endedLeases && (
                            <LeaseHistoryButton buildingKey={building.key} leases={endedLeases} getTenantName={getTenantName} />
                        )}
                    />

                    <RentablePhotosSection
                        photos={photos}
                        buildingKey={building.key}
                    />

                    {isAdmin && keyRegisterOn && <ObjectKeysButton onClick={() => setKeysOpen(true)} disabled={submitting} />}
                </Modal.Body>
            </Modal>
            {isAdmin && keyRegisterOn && keysOpen && (
                <ObjectKeysModal
                    show={show}
                    onHide={() => setKeysOpen(false)}
                    object={{ kind: 'storageUnit', key: unit.key, buildingKey: building.key }}
                    objectName={storageUnitDisplayName(unit)}
                />
            )}
        </>
    );
};

export default StorageUnitDetailModal;
